(ns app.youtube-posting.page
  "Replicant page for publishing YouTube Shorts."
  (:require [clojure.string :as str]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [ui.app-frame :as app-frame]))

(defn- youtube-publish-query
  [state]
  (let [eid (::story-media-eid state)]
    (when eid
      {:query/kind :query/youtube-publish
       :query/data {:story-media/eid eid}})))

(defn- youtube-title
  [state result]
  (or (::title state)
      (:story-media/name result)
      ""))

(defn- publish-command-data
  [state result]
  {:youtube.user/id (::selected-account-id state)
   :story-media/eid (:story-media/eid result)
   :blob-id (:story-media/blob-id result)
   :title (youtube-title state result)
   :description (::description state)
   :privacy-status (or (::privacy-status state) "public")
   :made-for-kids? (::made-for-kids state)
   :synthetic-media? (::synthetic-media state)})

(defn- schedule-command
  [state result date-ms]
  {:command/kind :command/youtube-short-schedule
   :command/data (cond-> (publish-command-data state result)
                   date-ms
                   (assoc :youtube.short/date date-ms))})

(defn- youtube-icon []
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :viewBox "0 0 576 512"
         :fill "currentColor"
         :class ["w-4" "h-4"]}
   [:path {:d "M549.7 124.1c-6.3-23.7-24.8-42.3-48.3-48.6C458.8 64 288 64 288 64S117.2 64 74.6 75.5c-23.5 6.3-42 24.9-48.3 48.6C15 167 15 256.4 15 256.4s0 89.4 11.3 132.3c6.3 23.7 24.8 41.5 48.3 47.8C117.2 448 288 448 288 448s170.8 0 213.4-11.5c23.5-6.3 42-24.1 48.3-47.8C561 345.8 561 256.4 561 256.4s0-89.4-11.3-132.3zM232 337.6V175.2l142 81.2-142 81.2z"}]])

(defn- preview-section
  [result]
  (let [preview-url (:story-media/preview-url result)
        video-url (:story-media/video-url result)]
    [:div
     {:class ["w-full" "sm:w-48" "md:w-56" "shrink-0"]}
     [:div
      {:class ["relative" "rounded-lg" "overflow-hidden" "bg-gray-900" "shadow-lg"]
       :style {:aspect-ratio "9/16"}}
      (if video-url
        [:video {:src video-url
                 :poster preview-url
                 :controls true
                 :class ["w-full" "h-full" "object-cover"]}]
        (if preview-url
          [:img {:src preview-url
                 :class ["w-full" "h-full" "object-cover" "opacity-70"]}]
          [:div {:class ["w-full" "h-full" "flex" "items-center" "justify-center"
                         "text-gray-500" "text-sm"]}
           "No video"]))]
     (when-not video-url
       [:div
        {:class ["mt-3" "rounded-lg" "border" "border-amber-200" "bg-amber-50"
                 "p-3" "text-xs" "text-amber-800"]}
        "YouTube Shorts require a rendered video. Prepare or render this media first."])]))

(defn- checkbox
  [{:keys [label description checked store-key]}]
  [:label
   {:class ["flex" "items-start" "gap-3" "py-2" "cursor-pointer"]}
   [:input
    {:type "checkbox"
     :checked (boolean checked)
     :class ["mt-0.5" "h-4" "w-4" "rounded" "border-gray-300"
             "text-red-600" "cursor-pointer"]
     :on {:click [[:store/assoc store-key (not checked)]]}}]
   [:div
    [:div {:class ["text-sm" "font-medium" "text-gray-900"]} label]
    (when description
      [:div {:class ["text-xs" "text-gray-500"]} description])]])

(defn- account-selector
  [state accounts]
  (let [selected-id (::selected-account-id state)]
    [:div
     {:class ["space-y-3"]}
     [:h3
      {:class ["text-sm" "font-semibold" "text-gray-700"]}
      "YouTube Channel"]
     (if (seq accounts)
       [:div
        {:class ["space-y-2"]}
        (map
          (fn [account]
            (let [account-id (:youtube.user/id account)
                  is-selected (= selected-id account-id)]
              [:div
               {:class (into ["flex" "items-center" "gap-3" "p-3" "rounded-lg" "border"
                              "cursor-pointer" "transition-colors"]
                             (if is-selected
                               ["border-red-600" "bg-red-50"]
                               ["border-gray-200" "bg-white" "hover:bg-gray-50"]))
                :replicant/key account-id
                :on {:click [[:store/assoc ::selected-account-id account-id]]}}
               [:div
                {:class ["w-10" "h-10" "rounded-full" "overflow-hidden" "bg-gray-200"
                         "shrink-0"]}
                (if (:youtube.user/profile-picture account)
                  [:img {:src (:youtube.user/profile-picture account)
                         :class ["w-full" "h-full" "object-cover"]}]
                  [:div {:class ["w-full" "h-full" "flex" "items-center" "justify-center"
                                 "text-red-500"]}
                   (youtube-icon)])]
               [:div
                {:class ["flex-1" "min-w-0"]}
                [:div
                 {:class ["text-sm" "font-medium" "text-gray-900" "truncate"]}
                 (:youtube.user/name account)]]
               [:div
                {:class (into ["w-5" "h-5" "rounded-full" "border-2" "flex" "items-center"
                               "justify-center" "shrink-0"]
                              (if is-selected
                                ["border-red-600" "bg-red-600"]
                                ["border-gray-300"]))}
                (when is-selected
                  [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
                         :fill "white" :class "w-3 h-3"}
                   [:path {:fill-rule "evenodd" :clip-rule "evenodd"
                           :d "M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"}]])]]))
          accounts)]
       [:div
        {:class ["text-sm" "text-gray-500" "p-4" "text-center"
                 "bg-gray-50" "rounded-lg" "border" "border-gray-200"]}
        "No YouTube channels connected."])
     [:a
      {:href "/ui/social-accounts"
       :class ["inline-flex" "items-center" "gap-1.5" "text-sm" "text-gray-700"
               "hover:text-gray-900" "font-medium"]}
      "+ Add channel"]]))

(def privacy-status-labels
  {"private" "Private"
   "unlisted" "Unlisted"
   "public" "Public"})

(defn- date-ms
  [date-str]
  (when (seq date-str)
    (let [date-ms (.getTime (js/Date. date-str))]
      (when-not (js/isNaN date-ms)
        date-ms))))

(defn- future-date-ms
  [date-str]
  (when-let [date-ms (date-ms date-str)]
    (when (> date-ms (.getTime (js/Date.)))
      date-ms)))

(defn- current-datetime-local
  []
  (let [now (js/Date.)
        pad (fn [n] (if (< n 10) (str "0" n) (str n)))]
    (str (.getFullYear now) "-"
         (pad (inc (.getMonth now))) "-"
         (pad (.getDate now)) "T"
         (pad (.getHours now)) ":"
         (pad (.getMinutes now)))))

(defn- duration-too-long?
  [result]
  (let [duration (:story-media/duration result)]
    (and (number? duration)
         (> duration 180))))

(defn- content-options
  [state result]
  (let [title (youtube-title state result)]
    [:div
     {:class ["space-y-4"]}
     [:div
      {:class ["space-y-2"]}
      [:label
       {:class ["text-sm" "font-medium" "text-gray-700"]}
       "Title"]
      [:input
       {:type "text"
        :value title
        :placeholder "Title for your YouTube Short"
        :maxLength 100
        :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                "rounded-lg" "focus:ring-2" "focus:ring-red-500" "focus:border-red-500"]
        :on {:input [[:store/assoc ::title :event/target.value]]}}]
      [:p
       {:class ["text-xs" "text-gray-500"]}
       (str (count title) "/100 characters")]]

     [:div
      {:class ["space-y-2"]}
      [:label
       {:class ["text-sm" "font-medium" "text-gray-700"]}
       "Description"]
      [:textarea
       {:value (or (::description state) "")
        :placeholder "Add a description and #hashtags ..."
        :rows 3
        :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                "rounded-lg" "focus:ring-2" "focus:ring-red-500" "focus:border-red-500"
                "resize-none"]
        :on {:input [[:store/assoc ::description :event/target.value]]}}]]

     [:div
      {:class ["space-y-2"]}
      [:label
       {:class ["text-sm" "font-medium" "text-gray-700"]}
       "Visibility"]
      [:select
       {:class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                "rounded-lg" "focus:ring-2" "focus:ring-red-500" "focus:border-red-500"]
        :on {:change [[:store/assoc ::privacy-status :event/target.value]]}}
       (map (fn [status]
              [:option {:value status
                        :selected (= status (or (::privacy-status state) "public"))
                        :replicant/key status}
               (get privacy-status-labels status status)])
            ["private" "unlisted" "public"])]]

     [:div
      {:class ["border-t" "border-gray-100" "pt-3" "space-y-1"]}
      (checkbox {:label "Made for kids"
                 :description "Declare that this Short is directed to children."
                 :checked (::made-for-kids state)
                 :store-key ::made-for-kids})
      (checkbox {:label "Altered or synthetic media"
                 :description "Disclose realistic AI-generated or altered content."
                 :checked (::synthetic-media state)
                 :store-key ::synthetic-media})]]))

(defn- publish-section
  [state result]
  (let [q (youtube-publish-query state)
        date-str (or (not-empty (::schedule-date-str state))
                     (current-datetime-local))
        schedule-date-ms (future-date-ms date-str)
        schedule? (some? schedule-date-ms)
        post-date-ms (or schedule-date-ms
                         (date-ms date-str)
                         (.getTime (js/Date.)))
        cmd (schedule-command state result post-date-ms)
        queued (::queued state)
        scheduled (::scheduled state)
        title-present? (seq (str/trim (youtube-title state result)))
        can-publish (and (:story-media/blob-id result)
                         title-present?
                         (::selected-account-id state)
                         (not (duration-too-long? result))
                         (not queued)
                         (not scheduled)
                         (or (:organization/positive-credit-balance? result)
                             (:organization/legacy-customer? result)))
        pricing (:youtube/pricing result)]
    [:div
     {:class ["space-y-4" "border-t" "border-gray-100" "pt-4"]}

     [:div
      {:class ["space-y-2"]}
      [:label
       {:class ["text-sm" "font-medium" "text-gray-700"]}
       "Post now or schedule"]
      [:input
       {:type "datetime-local"
        :value (or date-str (current-datetime-local))
        :class ["w-full" "px-3" "py-2" "text-sm" "border" "border-gray-300"
                "rounded-lg" "focus:ring-2" "focus:ring-red-500" "focus:border-red-500"]
        :on {:input [[:store/assoc ::schedule-date-str :event/target.value]
                     [:store/assoc ::queued nil]
                     [:store/assoc ::scheduled nil]]}}]
      [:p
       {:class ["text-xs" "text-gray-500"]}
       "Choose a future date to schedule, or leave it as now to post as soon as possible."]]

     (when (duration-too-long? result)
       [:div
        {:class ["p-3" "rounded-lg" "bg-red-50" "border" "border-red-200"]}
        [:p
         {:class ["text-sm" "text-red-800"]}
         "YouTube Shorts can be at most 3 minutes long."]])

     (when-not (:story-media/blob-id result)
       [:div
        {:class ["p-3" "rounded-lg" "bg-amber-50" "border" "border-amber-200"]}
        [:p
         {:class ["text-sm" "text-amber-800"]}
         "This media does not have a rendered video yet."]])

     (when-not title-present?
       [:div
        {:class ["p-3" "rounded-lg" "bg-amber-50" "border" "border-amber-200"]}
        [:p
         {:class ["text-sm" "text-amber-800"]}
         "Please add a title for the YouTube Short."]])

     (when queued
       [:div
        {:class ["p-3" "rounded-lg" "bg-green-50" "border" "border-green-200"]}
        [:p
         {:class ["text-sm" "text-green-800" "font-medium"]}
         "YouTube Short queued. It will appear below while it uploads."]])

     (when scheduled
       [:div
        {:class ["p-3" "rounded-lg" "bg-green-50" "border" "border-green-200"]}
        [:p
         {:class ["text-sm" "text-green-800" "font-medium"]}
         "YouTube Short scheduled successfully!"]])

     (when (command/error? state cmd)
       [:div
        {:class ["p-3" "rounded-lg" "bg-red-50" "border" "border-red-200"]}
        [:p
         {:class ["text-sm" "text-red-800"]}
         (or (command/get-latest-error state cmd)
             "Failed to queue YouTube Short. Please try again.")]])

     (when (and (not (:organization/positive-credit-balance? result))
                (not (:organization/legacy-customer? result)))
       [:div
        {:class ["p-3" "rounded-lg" "bg-amber-50" "border" "border-amber-200"]}
        [:p
         {:class ["text-sm" "text-amber-800"]}
         (if (:organization/free-account? result)
           [:span "You need a subscription to publish. "
            [:a {:href "/ui/subscription"
                 :class ["font-medium" "text-amber-900" "underline"]}
             "Subscribe now"]]
           [:span "Your credit balance is empty. "
            [:button
             {:class ["font-medium" "text-amber-900" "underline" "cursor-pointer"]
              :on {:click [[:data/query {:query/kind :query/user-session}]
                           [:ui/modal-open :modals/credits-exhausted {}]]}}
             "Top up credits"]])]])

     [:button
      {:class (into ["w-full" "py-2.5" "px-4" "text-sm" "font-semibold" "rounded-lg"
                     "transition-colors" "cursor-pointer" "inline-flex" "items-center"
                     "justify-center" "gap-2"]
                    (if can-publish
                      ["bg-red-600" "text-white" "hover:bg-red-700"]
                      ["bg-gray-300" "text-gray-500" "cursor-not-allowed"]))
       :disabled (or (not can-publish)
                     (command/issued? state cmd))
       :on {:click (when can-publish
                     [[:data/command
                       cmd
                       {:on-success [(if schedule?
                                       [:store/assoc ::scheduled true]
                                       [:store/assoc ::queued true])
                                     [:data/query q]]
                        :on-error [[:ui/show-error]]}]])}}
      (youtube-icon)
      (cond
        (command/issued? state cmd) (if schedule? "Scheduling..." "Queueing...")
        scheduled "Scheduled"
        queued "Queued for YouTube"
        schedule? "Schedule YouTube Short"
        :else "Post now")]

     (when (and can-publish
                (not (:organization/legacy-customer? result))
                pricing)
       (if-let [discount (:discount pricing)]
         [:div
          {:class ["flex" "items-center" "justify-center" "gap-2" "text-sm"]}
          [:span {:class ["text-gray-400" "line-through"]}
           (str "$" (:original-price discount))]
          [:span {:class ["font-semibold" "text-green-600"]}
           (str "$" (:discounted-price discount))]
          [:span {:class ["inline-flex" "items-center" "rounded-md" "bg-green-50"
                          "px-2" "py-0.5" "text-xs" "font-medium" "text-green-700"
                          "ring-1" "ring-inset" "ring-green-600/20"]}
           (str (.round js/Math (* (:discount-percentage discount) 100)) "% OFF")]]
         [:div
          {:class ["text-center" "text-sm" "text-gray-500"]}
          (str "Cost $" (:price pricing))]))]))

(defn- format-scheduled-date
  [date]
  (when date
    (.toLocaleString (js/Date. date))))

(defn- scheduled-status-label
  [status]
  (case status
    :scheduled "Scheduled"
    :in-progress "Uploading..."
    :processing "Processing on YouTube..."
    :executed "Uploaded"
    :error "Error"
    :canceled "Canceled"
    "Scheduled"))

(defn- youtube-video-link
  [video-id]
  (str "https://www.youtube.com/shorts/" video-id))

(defn- scheduled-shorts-section
  [state result]
  (let [q (youtube-publish-query state)
        posts (:youtube-posting/history result)]
    (when (seq posts)
      [:div
       {:class ["border-t" "border-gray-100" "pt-4" "space-y-3"]}
       [:h3
        {:class ["text-sm" "font-semibold" "text-gray-700"]}
        "YouTube Shorts"]
       (map
         (fn [post]
           (let [cancel-cmd {:command/kind :command/youtube-short-cancel
                             :command/data {:db/id (:db/id post)}}
                 cancelable? (= :scheduled (:status post))]
             [:div
              {:class ["flex" "items-center" "justify-between" "gap-3" "p-3"
                       "rounded-lg" "border" "border-gray-200" "bg-white"]
               :replicant/key (:db/id post)}
              [:div
               {:class ["min-w-0"]}
               [:div
                {:class ["text-sm" "font-medium" "text-gray-900"]}
                (scheduled-status-label (:status post))]
               [:div
                {:class ["text-xs" "text-gray-500"]}
                (format-scheduled-date (:youtube.short/date post))]
               (when-let [account-name (get-in post [:youtube.short/account :youtube.user/name])]
                 [:div
                  {:class ["text-xs" "text-gray-400" "truncate"]}
                  account-name])
               (when-let [video-id (:youtube.short/video-id post)]
                 [:a
                  {:href (youtube-video-link video-id)
                   :target "_blank"
                   :class ["text-xs" "text-red-600" "hover:text-red-700" "truncate"]}
                  video-id])
               (when-let [error (:youtube.short/error-message post)]
                 [:div
                  {:class ["text-xs" "text-red-600" "truncate"]
                   :title error}
                  error])]
              (when cancelable?
                [:button
                 {:class ["text-xs" "font-medium" "text-red-600" "hover:text-red-500"
                          "cursor-pointer" "shrink-0"]
                  :on {:click [[:js/confirm "Cancel this scheduled YouTube Short?"]
                               [:data/command cancel-cmd
                                {:on-success [[:data/query q]]
                                 :on-error [[:ui/show-error]]}]]}}
                 "Cancel"])]))
         posts)])))

(defn render
  [state]
  (let [q (youtube-publish-query state)
        result (when q (query/get-result state q))
        loading (and q
                     (query/loading? state q)
                     (not (query/available? state q)))]
    (cond
      (not q)
      [:div
       {:class ["flex" "items-center" "justify-center" "min-h-screen"]}
       [:div {:class ["text-gray-500"]}
        "No media selected. Please open this page from the media gallery."]]

      loading
      [:div
       {:class ["flex" "items-center" "justify-center" "min-h-screen"]}
       [:div {:class ["text-gray-500"]} "Loading..."]]

      :else
      (app-frame/app-frame
        state
        [:div
         {:class ["max-w-4xl" "mx-auto" "overflow-x-hidden"]}
         [:div
          {:class ["mb-4"]}
          [:h1
           {:class ["text-xl" "font-bold" "text-gray-900"]}
           (or (:story-media/name result) "YouTube Shorts")]
          [:p
           {:class ["text-sm" "text-gray-500" "mt-1"]}
           [:a
            {:href "/ui/agenda"
             :class ["text-red-600" "hover:text-red-700"]}
            "Back to schedule"]]]
         [:div
          {:class ["flex" "flex-col" "sm:flex-row" "gap-6"]}
          (preview-section result)
          [:div
           {:class ["flex-1" "space-y-6"]}
           (account-selector state (:youtube/accounts result))
           (when (::selected-account-id state)
             [:div
              {:class ["space-y-4"]}
              (content-options state result)
              (publish-section state result)
              (scheduled-shorts-section state result)])]]]))))

(defn on-load
  [location]
  (let [eid-str (get-in location [:location/query-params :story-eid])
        eid (when eid-str (js/parseInt eid-str 10))]
    (when (and eid (not (js/isNaN eid)))
      [[:ui/hide-sidebar-on-mobile]
       [:store/assoc ::story-media-eid eid]
       [:store/assoc ::queued nil]
       [:store/assoc ::scheduled nil]
       [:store/assoc ::schedule-date-str nil]
       [:store/assoc ::privacy-status "public"]
       [:data/query {:query/kind :query/youtube-publish
                     :query/data {:story-media/eid eid}}]])))

(def page
  {:page-id :pages/youtube-publish
   :route [["ui" "youtube-publish"]]
   :on-load on-load
   :render #'render})
